import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { Patient } from '@/types/patient-api';

/**
 * POST /api/Patients — create a new patient from the patient menu.
 *
 * Mirrors Flutter `createPatient()` in
 * `patient_data_source_impl.dart:332`:
 *
 *   await client.post(
 *     '${(await patientAPI(PatientService.Patients))}',
 *     data: patientBody,
 *   );
 *
 * Body strips three sub-resource arrays (Flutter parity):
 *   - `updatePatientContactRequest` — saved via a separate
 *     `POST /Patients/ContactDetails` after this returns the patientId
 *     (`useCreateContactDetails`)
 *   - `emergencyContactResponse` — saved via
 *     `POST /Patients/EmergencyContact` (`useSaveEmergencyContacts`)
 *   - `healthInsuranceResponse` — saved via `POST /Patients/HealthInsurance`
 *     (`useSaveHealthInsurance`)
 *
 * **DO NOT use `/api/Patients/CreateUpdatePerson`** for this — that
 * endpoint is reserved for the inline create-appointment patient flow
 * (`useCreatePatient`). The patient-menu Personal Details tab uses
 * `POST /api/Patients` per Flutter.
 *
 * Returns the freshly-saved Patient (full object — backend echoes back
 * with `patientId` + `patientMrn` populated).
 */
interface CreatePatientFromMenuCommand {
  patient: Patient;
}

export function useCreatePatientFromMenu() {
  const qc = useQueryClient();
  return useMutation<Patient, Error, CreatePatientFromMenuCommand>({
    mutationFn: ({ patient }) => post<Patient>('/api/Patients', toWireBody(patient)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patients-list'] });
    },
  });
}

function toWireBody(patient: Patient): Record<string, unknown> {
  // patientMRN casing — ONE-WAY rename on send. Same pattern as
  // `useUpdatePatient.toWireBody` — see that file's doc-block for the
  // GET (lowercase) / PUT-POST (uppercase) asymmetry and round-trip
  // safety analysis.
  //
  // Strips the three sub-resources (verified against POST /api/Patients
  // curl 2026-05-14): `updatePatientContactRequest` (goes through
  // POST /Patients/ContactDetails after this), `emergencyContactResponse`,
  // `healthInsuranceResponse`.
  //
  // `patientId` is explicitly null on create — backend distinguishes
  // create vs. update on that key.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    updatePatientContactRequest,
    emergencyContactResponse,
    healthInsuranceResponse,
    patientMrn,
    ...rest
  } = patient as Patient & {
    updatePatientContactRequest?: unknown;
    emergencyContactResponse?: unknown;
    healthInsuranceResponse?: unknown;
  };
  return {
    ...rest,
    patientId: null,
    patientMRN: patientMrn ?? null,
  };
}
